import React from "react";
import {
  AbsoluteFill,
  interpolate,
  spring,
  useCurrentFrame,
  useVideoConfig,
} from "remotion";
import { COLORS, FONTS, SPRING } from "./constants";
import { GridBackground } from "./components/GridBackground";
import { FlowArrow } from "./components/FlowArrow";
import { DataParticle } from "./components/DataParticle";

interface RAGStep {
  label: string;
  sublabel: string;
  icon: string;
  x: number;
  y: number;
  color: string;
  delay: number;
}

const MAIN_FLOW: RAGStep[] = [
  { label: "Query", sublabel: "User input", icon: "💬", x: 160, y: 400, color: COLORS.accentBlue, delay: 10 },
  { label: "Rewrite", sublabel: "LLM expansion", icon: "✏️", x: 430, y: 400, color: COLORS.accentPurple, delay: 35 },
  { label: "Search", sublabel: "Hybrid", icon: "🔎", x: 700, y: 400, color: COLORS.accentTeal, delay: 60 },
  { label: "Rerank", sublabel: "Cross-encoder", icon: "📊", x: 970, y: 400, color: COLORS.accentPink, delay: 100 },
  { label: "Generate", sublabel: "Grok 4.1 Fast", icon: "🤖", x: 1240, y: 400, color: COLORS.accentOrange, delay: 140 },
  { label: "Cite", sublabel: "Source links", icon: "📎", x: 1510, y: 400, color: COLORS.accentGreen, delay: 175 },
];

export const Scene5RAGQuery: React.FC = () => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();

  const titleAppear = spring({ frame, fps, config: SPRING.snappy });

  // Exit
  const exitOpacity = interpolate(frame, [330, 360], [1, 0], {
    extrapolateLeft: "clamp",
    extrapolateRight: "clamp",
  });

  return (
    <AbsoluteFill style={{ opacity: exitOpacity }}>
      <GridBackground opacity={0.06} />

      {/* Title */}
      <div
        style={{
          position: "absolute",
          top: 60,
          left: "50%",
          transform: "translate(-50%, 0)",
          textAlign: "center",
          opacity: interpolate(titleAppear, [0, 1], [0, 1]),
        }}
      >
        <div
          style={{
            fontSize: 42,
            fontFamily: FONTS.heading,
            fontWeight: 700,
            color: COLORS.textPrimary,
          }}
        >
          RAG Query{" "}
          <span style={{ color: COLORS.accentPurple }}>Pipeline</span>
        </div>
        <div
          style={{
            fontSize: 18,
            fontFamily: FONTS.body,
            color: COLORS.textSecondary,
            marginTop: 8,
          }}
        >
          Retrieval-Augmented Generation with hybrid search & reranking
        </div>
      </div>

      {/* Main flow boxes */}
      {MAIN_FLOW.map((step, i) => {
        const stepAppear = spring({
          frame: frame - step.delay,
          fps,
          config: SPRING.bouncy,
        });

        const opacity = interpolate(stepAppear, [0, 1], [0, 1]);
        const scale = interpolate(stepAppear, [0, 1], [0.5, 1]);
        const translateY = interpolate(stepAppear, [0, 1], [30, 0]);

        return (
          <React.Fragment key={i}>
            <div
              style={{
                position: "absolute",
                left: step.x - 90,
                top: step.y - 50,
                width: 180,
                height: 100,
                borderRadius: 16,
                background: `linear-gradient(135deg, ${step.color}18, ${step.color}06)`,
                border: `1.5px solid ${step.color}50`,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                gap: 6,
                opacity,
                transform: `scale(${scale}) translateY(${translateY}px)`,
                boxShadow: `0 0 25px ${step.color}12`,
              }}
            >
              <span style={{ fontSize: 28 }}>{step.icon}</span>
              <span
                style={{
                  fontFamily: FONTS.mono,
                  fontSize: 16,
                  fontWeight: 600,
                  color: COLORS.textPrimary,
                }}
              >
                {step.label}
              </span>
              <span
                style={{
                  fontFamily: FONTS.body,
                  fontSize: 12,
                  color: COLORS.textMuted,
                }}
              >
                {step.sublabel}
              </span>
            </div>

            {/* Arrows */}
            {i < MAIN_FLOW.length - 1 && (
              <FlowArrow
                fromX={step.x + 90}
                fromY={step.y}
                toX={MAIN_FLOW[i + 1].x - 90}
                toY={MAIN_FLOW[i + 1].y}
                delay={step.delay + 15}
                color={step.color}
                strokeWidth={2}
              />
            )}

            {/* Data particles */}
            {i < MAIN_FLOW.length - 1 && frame > step.delay + 25 && (
              <DataParticle
                fromX={step.x + 95}
                fromY={step.y}
                toX={MAIN_FLOW[i + 1].x - 95}
                toY={MAIN_FLOW[i + 1].y}
                delay={step.delay + 25}
                duration={30}
                color={step.color}
                size={4}
                count={2}
              />
            )}
          </React.Fragment>
        );
      })}

      {/* Hybrid Search detail (fork below Search box) */}
      {(() => {
        const detailDelay = 75;
        const vectorAppear = spring({
          frame: frame - detailDelay,
          fps,
          config: SPRING.snappy,
        });
        const bm25Appear = spring({
          frame: frame - detailDelay - 10,
          fps,
          config: SPRING.snappy,
        });

        return (
          <>
            {/* Vector branch */}
            <div
              style={{
                position: "absolute",
                left: 610,
                top: 560,
                opacity: interpolate(vectorAppear, [0, 1], [0, 1]),
                transform: `translateY(${interpolate(vectorAppear, [0, 1], [15, 0])}px)`,
              }}
            >
              <div
                style={{
                  padding: "10px 20px",
                  borderRadius: 10,
                  background: `${COLORS.accentTeal}12`,
                  border: `1px solid ${COLORS.accentTeal}30`,
                  fontFamily: FONTS.mono,
                  fontSize: 13,
                  color: COLORS.accentTeal,
                  textAlign: "center",
                }}
              >
                <div style={{ fontWeight: 600 }}>Vector Search</div>
                <div style={{ color: COLORS.textMuted, fontSize: 11, marginTop: 2 }}>
                  pgvector cosine · 70%
                </div>
              </div>
            </div>

            {/* BM25 branch */}
            <div
              style={{
                position: "absolute",
                left: 610,
                top: 640,
                opacity: interpolate(bm25Appear, [0, 1], [0, 1]),
                transform: `translateY(${interpolate(bm25Appear, [0, 1], [15, 0])}px)`,
              }}
            >
              <div
                style={{
                  padding: "10px 20px",
                  borderRadius: 10,
                  background: `${COLORS.accentYellow}12`,
                  border: `1px solid ${COLORS.accentYellow}30`,
                  fontFamily: FONTS.mono,
                  fontSize: 13,
                  color: COLORS.accentYellow,
                  textAlign: "center",
                }}
              >
                <div style={{ fontWeight: 600 }}>BM25 Search</div>
                <div style={{ color: COLORS.textMuted, fontSize: 11, marginTop: 2 }}>
                  tsvector FTS · 30%
                </div>
              </div>
            </div>

            {/* Fork arrows */}
            <FlowArrow
              fromX={700}
              fromY={450}
              toX={680}
              toY={555}
              delay={detailDelay - 5}
              color={COLORS.accentTeal}
              strokeWidth={1}
              dashed
            />
            <FlowArrow
              fromX={700}
              fromY={450}
              toX={680}
              toY={635}
              delay={detailDelay}
              color={COLORS.accentYellow}
              strokeWidth={1}
              dashed
            />
          </>
        );
      })()}

      {/* Guardrails badges */}
      {[
        { label: "🛡️ Input Guardrails", sublabel: "PII · Injection scan", x: 300, y: 200, delay: 50 },
        { label: "🔒 Access Control", sublabel: "Source permissions", x: 850, y: 200, delay: 110 },
        { label: "📊 Confidence Score", sublabel: "0.0 — 1.0", x: 1350, y: 200, delay: 160 },
      ].map((badge, i) => {
        const badgeAppear = spring({
          frame: frame - badge.delay,
          fps,
          config: SPRING.snappy,
        });
        return (
          <div
            key={i}
            style={{
              position: "absolute",
              left: badge.x,
              top: badge.y,
              opacity: interpolate(badgeAppear, [0, 1], [0, 1]),
              transform: `scale(${interpolate(badgeAppear, [0, 1], [0.7, 1])})`,
              padding: "8px 16px",
              borderRadius: 10,
              background: `${COLORS.bgCard}`,
              border: `1px solid ${COLORS.borderLight}`,
              textAlign: "center",
            }}
          >
            <div
              style={{
                fontFamily: FONTS.mono,
                fontSize: 14,
                color: COLORS.textSecondary,
              }}
            >
              {badge.label}
            </div>
            <div
              style={{
                fontFamily: FONTS.body,
                fontSize: 11,
                color: COLORS.textMuted,
                marginTop: 2,
              }}
            >
              {badge.sublabel}
            </div>
          </div>
        );
      })}

      {/* Latency breakdown */}
      {(() => {
        const latencyAppear = spring({
          frame: frame - 200,
          fps,
          config: SPRING.gentle,
        });
        return (
          <div
            style={{
              position: "absolute",
              bottom: 80,
              left: "50%",
              transform: "translate(-50%, 0)",
              opacity: interpolate(latencyAppear, [0, 1], [0, 1]),
              display: "flex",
              gap: 50,
            }}
          >
            {[
              { label: "Rewrite", value: "~800ms", color: COLORS.accentPurple },
              { label: "Search", value: "~150ms", color: COLORS.accentTeal },
              { label: "Rerank", value: "~400ms", color: COLORS.accentPink },
              { label: "LLM", value: "~2.5s", color: COLORS.accentOrange },
              { label: "Total", value: "~3-4s", color: COLORS.accentGreen },
            ].map((metric, i) => (
              <div key={i} style={{ textAlign: "center" }}>
                <div
                  style={{
                    fontFamily: FONTS.mono,
                    fontSize: 22,
                    fontWeight: 700,
                    color: metric.color,
                  }}
                >
                  {metric.value}
                </div>
                <div
                  style={{
                    fontFamily: FONTS.body,
                    fontSize: 13,
                    color: COLORS.textMuted,
                    marginTop: 2,
                  }}
                >
                  {metric.label}
                </div>
              </div>
            ))}
          </div>
        );
      })()}
    </AbsoluteFill>
  );
};

;
    var _remotion_globalVariableA;
    var _remotion_globalVariableB;
    // Legacy CSS implementations will `eval` browser code in a Node.js context
    // to extract CSS. For backwards compatibility, we need to check we're in a
    // browser context before continuing.
    if (typeof self !== 'undefined' &&
        // AMP / No-JS mode does not inject these helpers:
        '$RefreshHelpers$' in self) {
        const currentExports = __webpack_module__.exports;
        const prevExports = (_remotion_globalVariableB = (_remotion_globalVariableA = __webpack_module__.hot.data) === null || _remotion_globalVariableA === void 0 ? void 0 : _remotion_globalVariableA.prevExports) !== null && _remotion_globalVariableB !== void 0 ? _remotion_globalVariableB : null;
        // This cannot happen in MainTemplate because the exports mismatch between
        // templating and execution.
        self.$RefreshHelpers$.registerExportsForReactRefresh(currentExports, __webpack_module__.id);
        // A module can be accepted automatically based on its exports, e.g. when
        // it is a Refresh Boundary.
        if (self.$RefreshHelpers$.isReactRefreshBoundary(currentExports)) {
            // Save the previous exports on update so we can compare the boundary
            // signatures.
            __webpack_module__.hot.dispose((data) => {
                data.prevExports = currentExports;
            });
            // Unconditionally accept an update to this module, we'll check if it's
            // still a Refresh Boundary later.
            __webpack_module__.hot.accept();
            // This field is set when the previous version of this module was a
            // Refresh Boundary, letting us know we need to check for invalidation or
            // enqueue an update.
            if (prevExports !== null) {
                // A boundary can become ineligible if its exports are incompatible
                // with the previous exports.
                //
                // For example, if you add/remove/change exports, we'll want to
                // re-execute the importing modules, and force those components to
                // re-render. Similarly, if you convert a class component to a
                // function, we want to invalidate the boundary.
                if (self.$RefreshHelpers$.shouldInvalidateReactRefreshBoundary(prevExports, currentExports)) {
                    __webpack_module__.hot.invalidate();
                }
                else {
                    self.$RefreshHelpers$.scheduleUpdate();
                }
            }
        }
        else {
            // Since we just executed the code for the module, it's possible that the
            // new exports made it ineligible for being a boundary.
            // We only care about the case when we were _previously_ a boundary,
            // because we already accepted this update (accidental side effect).
            const isNoLongerABoundary = prevExports !== null;
            if (isNoLongerABoundary) {
                __webpack_module__.hot.invalidate();
            }
        }
    }
